import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const MESES = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const EMPLEADO_VACIO = { nombre: '', cedula: '', cargo: '', fecha_ingreso: '' };

const DATOS_VACIOS = {
  sueldo: '', auxilio_transporte: '', comisiones: '', horas_extras: '', recargos: '',
  otros_auxilios: '', bonificaciones: '', viaticos: '', exceso_alimentacion: '',
  incapacidades: '', licencia_maternidad: '', vacaciones: '', primas_extralegales: '',
  otros_ingresos_laborales: '', prima: '',
  aportes_pension_obligatorio: '', fondo_solidaridad_pensional: '', aportes_pension_voluntario: '', aportes_salud: '',
  interes_vivienda: '', salud_prepagada: '', tiene_dependientes: false,
  aportes_pension_voluntaria_126: '', aportes_afc_avc: '', indemnizacion_accidente: '',
  indemnizacion_maternidad: '', gastos_entierro: '', otras_rentas_exentas: '',
};

const GRUPO_INGRESOS = [
  ['sueldo', 'Sueldo'], ['auxilio_transporte', 'Auxilio de transporte'], ['comisiones', 'Comisiones'],
  ['horas_extras', 'Horas extras'], ['recargos', 'Recargos noct./dom.'], ['otros_auxilios', 'Otros auxilios'],
  ['bonificaciones', 'Bonificaciones'], ['viaticos', 'Viáticos'],
  ['exceso_alimentacion', 'Exceso alimentación (Art. 387-1)'], ['incapacidades', 'Incapacidades'],
  ['licencia_maternidad', 'Licencia maternidad/paternidad'], ['vacaciones', 'Vacaciones'],
  ['primas_extralegales', 'Primas extralegales'], ['otros_ingresos_laborales', 'Otros ingresos laborales'],
];

const GRUPO_NO_CONSTITUTIVOS = [
  ['aportes_pension_obligatorio', 'Aportes obligatorios a pensión'],
  ['fondo_solidaridad_pensional', 'Fondo de solidaridad pensional'],
  ['aportes_pension_voluntario', 'Aportes voluntarios a pensión obligatoria'],
  ['aportes_salud', 'Aportes obligatorios a salud'],
];

const GRUPO_DEDUCCIONES = [
  ['interes_vivienda', 'Intereses de vivienda (máx. 100 UVT/mes)'],
  ['salud_prepagada', 'Salud prepagada (máx. 16 UVT/mes)'],
];

const GRUPO_RENTAS_EXENTAS = [
  ['aportes_pension_voluntaria_126', 'Aportes voluntarios pensión (Art. 126-1)'],
  ['aportes_afc_avc', 'Aportes AFC/AVC (Art. 126-4)'],
  ['indemnizacion_accidente', 'Indemnización por accidente de trabajo'],
  ['indemnizacion_maternidad', 'Indemnización por maternidad'],
  ['gastos_entierro', 'Gastos de entierro del trabajador'],
  ['otras_rentas_exentas', 'Otras rentas exentas Art. 206 ET'],
];

const money = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('es-CO', { maximumFractionDigits: 0 });
};

export default function RetencionFuente() {
  const { user, logout, API } = useAuth();

  const [empleados, setEmpleados] = useState([]);
  const [empleadoId, setEmpleadoId] = useState(null);
  const [mostrarFormEmpleado, setMostrarFormEmpleado] = useState(false);
  const [empleadoEditId, setEmpleadoEditId] = useState(null);
  const [formEmpleado, setFormEmpleado] = useState(EMPLEADO_VACIO);

  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [datos, setDatos] = useState(DATOS_VACIOS);

  const [resultado, setResultado] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [liquidacionId, setLiquidacionId] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [msg, setMsg] = useState('');
  const debounceRef = useRef(null);

  const puedeEscribir = ['admin', 'usuario', 'digitador'].includes(user?.rol);

  const cargarEmpleados = useCallback(() => {
    axios.get(`${API}/retencion/empleados`).then(r => setEmpleados(r.data)).catch(() => {});
  }, [API]);

  const cargarHistorial = useCallback((empId) => {
    if (!empId) { setHistorial([]); return; }
    axios.get(`${API}/retencion/liquidaciones`, { params: { empleado_id: empId } })
      .then(r => setHistorial(r.data)).catch(() => {});
  }, [API]);

  useEffect(() => { cargarEmpleados(); }, [cargarEmpleados]);
  useEffect(() => { cargarHistorial(empleadoId); }, [empleadoId, cargarHistorial]);

  // Cálculo en vivo (con debounce) cada vez que cambian los datos, el período o el empleado.
  useEffect(() => {
    if (!empleadoId) { setResultado(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCalculando(true);
      const datosNum = Object.fromEntries(
        Object.entries(datos).map(([k, v]) => [k, typeof v === 'boolean' ? v : (Number(v) || 0)])
      );
      axios.post(`${API}/retencion/calcular`, { empleado_id: empleadoId, anio, mes, datos: datosNum })
        .then(r => setResultado(r.data))
        .catch((e) => setMsg(e.response?.data?.detail || 'No se pudo calcular'))
        .finally(() => setCalculando(false));
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [datos, anio, mes, empleadoId, API]);

  const handleDato = (e) => {
    const { name, value, type, checked } = e.target;
    setDatos(d => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
    setLiquidacionId(null);
  };

  const seleccionarEmpleado = (id) => {
    setEmpleadoId(id);
    setDatos(DATOS_VACIOS);
    setLiquidacionId(null);
    setMsg('');
  };

  const abrirFormEmpleado = (emp) => {
    if (emp) { setFormEmpleado({ ...emp }); setEmpleadoEditId(emp.id); }
    else { setFormEmpleado(EMPLEADO_VACIO); setEmpleadoEditId(null); }
    setMostrarFormEmpleado(true);
  };

  const guardarEmpleado = async () => {
    if (!formEmpleado.nombre.trim()) { setMsg('El nombre del empleado es obligatorio'); return; }
    try {
      if (empleadoEditId) {
        await axios.put(`${API}/retencion/empleados/${empleadoEditId}`, {
          nombre: formEmpleado.nombre, cedula: formEmpleado.cedula,
          cargo: formEmpleado.cargo, fecha_ingreso: formEmpleado.fecha_ingreso,
        }, { params: { activo: formEmpleado.activo ?? 1 } });
      } else {
        const r = await axios.post(`${API}/retencion/empleados`, formEmpleado);
        seleccionarEmpleado(r.data.id);
      }
      setMostrarFormEmpleado(false);
      cargarEmpleados();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al guardar el empleado');
    }
  };

  const eliminarEmpleado = async (id) => {
    if (!window.confirm('¿Eliminar este empleado?')) return;
    try {
      await axios.delete(`${API}/retencion/empleados/${id}`);
      if (empleadoId === id) seleccionarEmpleado(null);
      cargarEmpleados();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const guardarLiquidacion = async () => {
    setGuardando(true);
    setMsg('');
    try {
      const datosNum = Object.fromEntries(
        Object.entries(datos).map(([k, v]) => [k, typeof v === 'boolean' ? v : (Number(v) || 0)])
      );
      const r = await axios.post(`${API}/retencion/liquidaciones`, { empleado_id: empleadoId, anio, mes, datos: datosNum });
      setLiquidacionId(r.data.id);
      setResultado(r.data.resultado);
      setMsg('Liquidación guardada.');
      cargarHistorial(empleadoId);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al guardar la liquidación');
    } finally {
      setGuardando(false);
    }
  };

  const descargar = async (id, tipo) => {
    const r = await axios.get(`${API}/retencion/liquidaciones/${id}/${tipo}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    const cd = r.headers['content-disposition'] || '';
    // Starlette usa filename*=utf-8''nombre-codificado (RFC 5987) cuando el
    // nombre tiene acentos/ñ; solo cae a filename="..." con nombres ASCII.
    const matchUtf8 = cd.match(/filename\*=utf-8''([^;]+)/i);
    const matchAscii = cd.match(/filename="?([^";]+)"?/i);
    const nombre = matchUtf8 ? decodeURIComponent(matchUtf8[1]) : (matchAscii ? matchAscii[1] : null);
    a.download = nombre || `retencion.${tipo === 'excel' ? 'xlsx' : 'pdf'}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const cargarDesdeHistorial = (liq) => {
    setAnio(liq.anio);
    setMes(liq.mes);
    setDatos({ ...DATOS_VACIOS, ...liq.datos });
    setLiquidacionId(liq.id);
    setMsg('');
  };

  const eliminarLiquidacion = async (id) => {
    if (!window.confirm('¿Eliminar esta liquidación del historial?')) return;
    await axios.delete(`${API}/retencion/liquidaciones/${id}`);
    if (liquidacionId === id) setLiquidacionId(null);
    cargarHistorial(empleadoId);
  };

  const empleado = empleados.find(e => e.id === empleadoId);

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.brand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <a href="/" style={s.navLink}>⚡ Conciliar</a>
          <a href="/kpis" style={s.navLink}>📊 KPIs</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <a href="/cuentas" style={s.navLink}>🏦 Cuentas</a>
          <a href="/calendario-tributario" style={s.navLink}>📅 Calendario</a>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <span style={s.navUser}>👤 {user?.nombre}</span>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>🧾 Retención en la fuente por salarios</h1>
            <p style={s.subtitle}>Procedimiento 1 (mensual) — Art. 383, 387 y 206 del Estatuto Tributario.</p>
          </div>
        </div>

        <div style={s.layout}>
          {/* ── Columna: empleados ── */}
          <div style={s.sidebar}>
            <div style={s.sidebarHead}>
              <span style={s.sidebarTitle}>Empleados</span>
              {puedeEscribir && <button style={s.btnMini} onClick={() => abrirFormEmpleado(null)}>+ Nuevo</button>}
            </div>
            {empleados.length === 0 && <p style={s.emptySmall}>No hay empleados registrados.</p>}
            <div style={s.empList}>
              {empleados.map(e => (
                <div key={e.id}
                  style={{ ...s.empItem, ...(e.id === empleadoId ? s.empItemActivo : {}), opacity: e.activo ? 1 : 0.5 }}
                  onClick={() => seleccionarEmpleado(e.id)}>
                  <div style={s.empNombre}>{e.nombre}{!e.activo && ' (inactivo)'}</div>
                  <div style={s.empDetalle}>{e.cargo || '—'} {e.cedula && `· CC ${e.cedula}`}</div>
                  {puedeEscribir && (
                    <div style={s.empAcciones}>
                      <button style={s.linkMini} onClick={(ev) => { ev.stopPropagation(); abrirFormEmpleado(e); }}>Editar</button>
                      <button style={s.linkMiniDel} onClick={(ev) => { ev.stopPropagation(); eliminarEmpleado(e.id); }}>Eliminar</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {mostrarFormEmpleado && (
              <div style={s.formEmp}>
                <div style={s.formEmpTitle}>{empleadoEditId ? 'Editar empleado' : 'Nuevo empleado'}</div>
                <input style={s.input} placeholder="Nombre *" value={formEmpleado.nombre}
                  onChange={e => setFormEmpleado(f => ({ ...f, nombre: e.target.value }))} />
                <input style={s.input} placeholder="Cédula" value={formEmpleado.cedula}
                  onChange={e => setFormEmpleado(f => ({ ...f, cedula: e.target.value }))} />
                <input style={s.input} placeholder="Cargo" value={formEmpleado.cargo}
                  onChange={e => setFormEmpleado(f => ({ ...f, cargo: e.target.value }))} />
                <input style={s.input} type="date" placeholder="Fecha de ingreso" value={formEmpleado.fecha_ingreso || ''}
                  onChange={e => setFormEmpleado(f => ({ ...f, fecha_ingreso: e.target.value }))} />
                {empleadoEditId && (
                  <label style={s.checkboxRow}>
                    <input type="checkbox" checked={!!(formEmpleado.activo ?? 1)}
                      onChange={e => setFormEmpleado(f => ({ ...f, activo: e.target.checked ? 1 : 0 }))} />
                    Activo
                  </label>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button style={s.btnPrimarySm} onClick={guardarEmpleado}>Guardar</button>
                  <button style={s.btnSecondarySm} onClick={() => setMostrarFormEmpleado(false)}>Cancelar</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Columna: liquidación ── */}
          <div style={s.main}>
            {!empleadoId ? (
              <div style={s.empty}>
                <div style={{ fontSize: 48 }}>🧾</div>
                <p>Selecciona o crea un empleado para liquidar su retención.</p>
              </div>
            ) : (
              <>
                <div style={s.card}>
                  <div style={s.cardHeadRow}>
                    <div>
                      <div style={s.empleadoTitulo}>{empleado?.nombre}</div>
                      <div style={s.empleadoSub}>{empleado?.cargo}{empleado?.cedula && ` · CC ${empleado.cedula}`}</div>
                    </div>
                    <div style={s.periodo}>
                      <select style={s.select} value={mes} onChange={e => setMes(Number(e.target.value))} disabled={!puedeEscribir}>
                        {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                      </select>
                      <input style={{ ...s.input, width: 80 }} type="number" value={anio}
                        onChange={e => setAnio(Number(e.target.value))} disabled={!puedeEscribir} />
                    </div>
                  </div>

                  {!puedeEscribir && (
                    <p style={s.avisoLectura}>Tu rol solo tiene acceso de lectura a esta función.</p>
                  )}

                  <Seccion titulo="Ingresos laborales del mes">
                    <Grid campos={GRUPO_INGRESOS} datos={datos} onChange={handleDato} disabled={!puedeEscribir} />
                    <div style={s.fieldSolo}>
                      <label style={s.label}>Prima de servicios (se liquida aparte)</label>
                      <input style={s.input} type="number" name="prima" value={datos.prima}
                        onChange={handleDato} disabled={!puedeEscribir} />
                    </div>
                  </Seccion>

                  <Seccion titulo="Ingresos no constitutivos de renta">
                    <Grid campos={GRUPO_NO_CONSTITUTIVOS} datos={datos} onChange={handleDato} disabled={!puedeEscribir} />
                  </Seccion>

                  <Seccion titulo="Deducciones (Art. 387 ET)">
                    <Grid campos={GRUPO_DEDUCCIONES} datos={datos} onChange={handleDato} disabled={!puedeEscribir} />
                    <label style={s.checkboxRow}>
                      <input type="checkbox" name="tiene_dependientes" checked={!!datos.tiene_dependientes}
                        onChange={handleDato} disabled={!puedeEscribir} />
                      Tiene dependientes (deducción de hasta 10% del ingreso, máx. 32 UVT/mes)
                    </label>
                  </Seccion>

                  <Seccion titulo="Rentas exentas (Art. 206 ET)">
                    <Grid campos={GRUPO_RENTAS_EXENTAS} datos={datos} onChange={handleDato} disabled={!puedeEscribir} />
                  </Seccion>

                  {msg && <p style={s.msg}>{msg}</p>}
                </div>

                {/* ── Resultado en vivo ── */}
                <div style={s.resultCard}>
                  <div style={s.resultTitle}>
                    Resultado {calculando && <span style={s.calculando}>calculando…</span>}
                  </div>
                  {resultado ? (
                    <>
                      <ResultRow label="Subtotal A (ingresos - no constitutivos)" val={resultado.subtotal_a} />
                      <ResultRow label="Subtotal B (- deducciones - rentas exentas)" val={resultado.subtotal_b} />
                      <ResultRow label="Renta exenta 25% aplicada (salario)" val={resultado.renta_exenta_25_salario} />
                      <ResultRow label="Base gravable salario" val={resultado.base_gravable} />
                      <ResultRow label="Retención sobre salario" val={resultado.retencion_salario} destacado />
                      {Number(datos.prima) > 0 && (
                        <>
                          <ResultRow label="Base gravable prima" val={resultado.base_gravable_prima} />
                          <ResultRow label="Retención sobre prima" val={resultado.retencion_prima} destacado />
                        </>
                      )}
                      <div style={s.totalRow}>
                        <span>Total retención del mes</span>
                        <span style={s.totalVal}>$ {money(resultado.total_retencion)}</span>
                      </div>
                      <div style={s.uvtNota}>
                        UVT {anio}: $ {money(resultado.uvt)} · Renta exenta 25% acumulada este año: $ {money(resultado.renta_exenta_25_usada_ytd_acumulado)} de un tope de $ {money(790 * resultado.uvt)}
                      </div>
                    </>
                  ) : (
                    <p style={s.emptySmall}>Ingresa los datos del mes para ver el cálculo.</p>
                  )}

                  {puedeEscribir && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                      <button style={s.btnPrimary} onClick={guardarLiquidacion} disabled={guardando || !resultado}>
                        {guardando ? 'Guardando...' : 'Guardar liquidación'}
                      </button>
                      {liquidacionId && (
                        <>
                          <button style={s.btnSecondary} onClick={() => descargar(liquidacionId, 'excel')}>⬇️ Excel</button>
                          <button style={s.btnSecondary} onClick={() => descargar(liquidacionId, 'pdf')}>⬇️ PDF</button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Historial ── */}
                <div style={s.card}>
                  <div style={s.formEmpTitle}>Historial de liquidaciones</div>
                  {historial.length === 0 ? (
                    <p style={s.emptySmall}>Aún no hay liquidaciones guardadas para este empleado.</p>
                  ) : (
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Período</th>
                          <th style={s.th}>Retención salario</th>
                          <th style={s.th}>Retención prima</th>
                          <th style={s.th}>Total</th>
                          <th style={s.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map(h => (
                          <tr key={h.id}>
                            <td style={s.td}>{MESES[h.mes]} {h.anio}</td>
                            <td style={s.td}>$ {money(h.retencion_salario)}</td>
                            <td style={s.td}>$ {money(h.retencion_prima)}</td>
                            <td style={{ ...s.td, fontWeight: 600 }}>$ {money(h.total_retencion)}</td>
                            <td style={s.td}>
                              <button style={s.linkMini} onClick={() => cargarDesdeHistorial(h)}>Ver/editar</button>{' '}
                              <button style={s.linkMini} onClick={() => descargar(h.id, 'excel')}>Excel</button>{' '}
                              <button style={s.linkMini} onClick={() => descargar(h.id, 'pdf')}>PDF</button>{' '}
                              {puedeEscribir && <button style={s.linkMiniDel} onClick={() => eliminarLiquidacion(h.id)}>Eliminar</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={s.section}>{titulo}</div>
      {children}
    </div>
  );
}

function Grid({ campos, datos, onChange, disabled }) {
  return (
    <div style={s.formGrid}>
      {campos.map(([k, lbl]) => (
        <div key={k} style={s.field}>
          <label style={s.label}>{lbl}</label>
          <input style={s.input} type="number" name={k} value={datos[k]} onChange={onChange} disabled={disabled} placeholder="0" />
        </div>
      ))}
    </div>
  );
}

function ResultRow({ label, val, destacado }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', color: destacado ? '#1F4E79' : '#444', fontWeight: destacado ? 600 : 400 }}>
      <span>{label}</span>
      <span>$ {money(val)}</span>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
    background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '28px 24px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: '0 0 4px', color: '#111' },
  subtitle: { fontSize: 13, color: '#666', margin: 0 },
  layout: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' },
  sidebar: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 16 },
  sidebarHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  sidebarTitle: { fontSize: 13, fontWeight: 700, color: '#111' },
  empList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' },
  empItem: { padding: '8px 10px', borderRadius: 9, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fafafa' },
  empItemActivo: { background: '#eff6ff', border: '1px solid #93c5fd' },
  empNombre: { fontSize: 13, fontWeight: 600, color: '#111' },
  empDetalle: { fontSize: 11, color: '#777', marginTop: 2 },
  empAcciones: { display: 'flex', gap: 10, marginTop: 4 },
  emptySmall: { fontSize: 12, color: '#888', textAlign: 'center', padding: '12px 0' },
  formEmp: { marginTop: 14, paddingTop: 14, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 },
  formEmpTitle: { fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 10 },
  main: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 20 },
  cardHeadRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  empleadoTitulo: { fontSize: 16, fontWeight: 700, color: '#111' },
  empleadoSub: { fontSize: 12, color: '#666' },
  periodo: { display: 'flex', gap: 8 },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 },
  empty: { textAlign: 'center', padding: '60px 0', color: '#888', display: 'flex',
    flexDirection: 'column', alignItems: 'center', gap: 12, background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb' },
  section: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 10, marginTop: 4, paddingBottom: 6,
    borderBottom: '1px solid #f0f0f0' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldSolo: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10, maxWidth: 260 },
  label: { fontSize: 11, color: '#666', fontWeight: 500 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', margin: '10px 0 0', cursor: 'pointer' },
  avisoLectura: { fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12 },
  msg: { color: '#2563eb', fontSize: 13, margin: '8px 0 0' },
  resultCard: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 20 },
  resultTitle: { fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 8 },
  calculando: { fontSize: 11, color: '#999', fontWeight: 400, marginLeft: 8 },
  totalRow: { display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10,
    borderTop: '1px solid #f0f0f0', fontSize: 15, fontWeight: 700, color: '#1F4E79' },
  totalVal: {},
  uvtNota: { fontSize: 11, color: '#888', marginTop: 8 },
  btnMini: { fontSize: 11, padding: '4px 10px', borderRadius: 7, background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer' },
  linkMini: { fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  linkMiniDel: { fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  btnPrimary: { padding: '10px 20px', borderRadius: 9, background: '#2563eb', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  btnSecondary: { padding: '10px 20px', borderRadius: 9, background: '#fff', color: '#444',
    border: '1px solid #e5e7eb', fontWeight: 500, fontSize: 14, cursor: 'pointer' },
  btnPrimarySm: { padding: '6px 14px', borderRadius: 8, background: '#2563eb', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  btnSecondarySm: { padding: '6px 14px', borderRadius: 8, background: '#fff', color: '#444',
    border: '1px solid #e5e7eb', fontWeight: 500, fontSize: 12, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #f0f0f0', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '8px 6px', borderBottom: '1px solid #f5f5f5' },
};
